// Instructor notes for Lesson 1 (#13, #24): one step at a time, each with a
// note and something on the scope to point at. A step is done once its
// condition holds; the first step not done yet is the one shown. Steps are
// data, so later lessons can bring their own.

import { FIELDS, SPRAY_WIND_MIN, SPRAY_WIND_MAX, ORIENTATION_WIND_SPEED } from '../config/constants.js';

const centre = (fieldIdx) => {
  const f = FIELDS[fieldIdx];
  return { x: f.x + f.w / 2, y: f.y + f.h / 2 };
};

export function lessonOneSteps() {
  const scout = (s) => s.ops.aircraft.find((a) => a.type === 'scout');
  const duster = (s) => s.ops.aircraft.find((a) => a.type === 'duster');
  const field = (s, n) => s.ops.fields[n];
  const isSelected = (sel, a) => sel?.kind === 'ops' && sel.id === a.id;
  const pick = (s, sel, a, n) => (isSelected(sel, a) ? centre(field(s, n).idx) : a);

  const warned = (s) => s.ops.aircraft.find((a) => a.alert || a.predict);
  return [
    {
      // steps in whenever two aircraft are about to come too close
      text: (s) => {
        const a = warned(s);
        const other = a.predict?.with ?? 'another aircraft';
        return a.alert
          ? `${a.call} is inside the separation ring! Select it and Hold (H) now.`
          : `${a.call} and ${other} will come too close in ${Math.ceil(a.predict.in)}s. Select one and Hold it (H), then Clear it (C) once the other has passed.`;
      },
      target: (s) => { const a = warned(s); return a.predict?.at ?? a; },
      done: (s) => !warned(s),
    },
    {
      // ...and doesn't leave a held aircraft circling forever
      text: (s) => {
        const a = s.ops.aircraft.find((x) => x.state === 'holding');
        return `${a.call} is holding, so it's safe but not working. Once the way is clear, select it and Clear it (C) to carry on.`;
      },
      target: (s) => s.ops.aircraft.find((x) => x.state === 'holding'),
      done: (s) => !s.ops.aircraft.some((x) => x.state === 'holding'),
    },
    {
      text: (s) => `Your scout drone, ${scout(s).call}, is parked at ${scout(s).padName}. Click it to select it.`,
      target: (s) => scout(s),
      done: (s, sel) => isSelected(sel, scout(s)) || field(s, 0).state !== 'unscouted',
    },
    {
      text: (s) => `Now click ${field(s, 0).name}. The scout flies a few wide passes over it to make sure the field is clear of people and vehicles before anyone sprays.`,
      target: (s, sel) => pick(s, sel, scout(s), 0),
      done: (s) => field(s, 0).state !== 'unscouted',
    },
    {
      text: () => `While it sweeps, look at the wind: a crop duster only sprays in ${SPRAY_WIND_MIN}–${SPRAY_WIND_MAX} mph wind. Calmer risks an inversion and drift; windier blows spray off the field. Today it's a steady ${ORIENTATION_WIND_SPEED} mph.`,
      target: (s) => scout(s),
      done: (s) => ['clear', 'spraying', 'sprayed'].includes(field(s, 0).state),
    },
    {
      text: (s) => `${field(s, 0).name} is clear. Select ${duster(s).call} at ${duster(s).padName}, then click ${field(s, 0).name} to spray it.`,
      target: (s, sel) => pick(s, sel, duster(s), 0),
      done: (s) => ['spraying', 'sprayed'].includes(field(s, 0).state),
    },
    {
      text: (s) => `The duster sprays tight lanes back and forth, then an edge pass. Meanwhile, send ${scout(s).call} to scout ${field(s, 1).name}.`,
      target: (s, sel) => pick(s, sel, scout(s), 1),
      done: (s) => field(s, 1).state !== 'unscouted',
    },
    {
      text: (s) => `Two aircraft working at once: watch their routes. If they come close, a red ring appears. Select one and Hold it (H), then Clear it (C) once the other has passed. When ${field(s, 1).name} is clear, send ${duster(s).call} to spray it.`,
      target: (s, sel) => (field(s, 1).state === 'clear' ? pick(s, sel, duster(s), 1) : null),
      done: (s) => ['spraying', 'sprayed'].includes(field(s, 1).state),
    },
    {
      text: () => 'Last field in progress. Lesson 1 is complete when both fields are sprayed and nobody came too close.',
      target: () => null,
      done: () => false,
    },
  ];
}

export function createCoach(boxEl, textEl) {
  let steps = null;
  let lastText = '';
  return {
    /** Start a lesson's steps, or none (null) for a shift without a lesson. */
    start(newSteps) {
      steps = newSteps;
      lastText = '';
      boxEl.hidden = !steps;
    },
    /** Show the current step; returns the scope point to highlight, or null. */
    update(s, selection) {
      if (!steps || !s.ops || !s.running) { boxEl.hidden = true; return null; }
      const stepNow = steps.find((st) => !st.done(s, selection)) ?? steps[steps.length - 1];
      const text = stepNow.text(s);
      if (text !== lastText) { textEl.textContent = text; lastText = text; }
      boxEl.hidden = false;
      return stepNow.target(s, selection);
    },
  };
}
